import { Button, Container, Divider, Stack, TextField, Typography } from "@mui/material";
import { useReducer } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { CompanyType } from "@entrilia/shared/api/clientTypes";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import useUserContext from "../../../../context/useUserContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import CompanyLogomarkSettings from "./CompanyLogomarkSettings";
import CompanyTypeSelect from "./CompanyTypeSelect";
import EntityLevelAccess from "./EntityLevelAccess";
import RefreshDemoCompanyButton from "./RefreshDemoCompanyButton";
import { getDemoRefreshTemplateCode } from "./demoRefreshHelper";
import { getInitialState, reducer } from "./generalCompanySettingsState";

const updateCompanySettings = withErrorHandling(adminApi.updateCompanySettings);

const GeneralCompanySettingsPage = () => {
  usePageTitle("Settings");

  const {
    clientCode,
    title: clientTitle,
    branding,
    entityLevelAccess,
    hasPermissions,
    companyType,
    sourceTemplateClientCode,
    isPortalFeatureEnabled,
  } = useClientContext();
  const { updateClientData } = useUserContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const hasAccessToManageCompanySettings = hasPermissions(["ManageCompanySettings"]);
  const investorPortalUrl = `${window.location.origin}/${clientCode}`;

  const [state, dispatch] = useReducer(
    reducer,
    getInitialState({
      entityLevelAccess: entityLevelAccess || false,
      companyType: companyType,
      logomarkUrl: branding?.logoMarkUrl,
    })
  );

  const handleSave = async () => {
    dispatch({ type: "SetIsLoading", payload: true });
    const [resp, error] = await updateCompanySettings(state.changes);
    dispatch({ type: "SetIsLoading", payload: false });

    if (error) {
      logError(error, "[GeneralCompanySettingsPage] updateCompanySettings");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Settings saved");
    dispatch({ type: "SetSavedSettings", payload: { ...resp } });
    updateClientData(clientCode, resp);
  };

  const handleCompanyTypeChange = (value: CompanyType) => {
    dispatch({ type: "UpdateSettings", payload: { companyType: value } });
  };

  const handleLogoChange = (file: File | undefined, url: string) => {
    if (file === undefined) {
      dispatch({ type: "ResetLogo" });
    } else {
      dispatch({ type: "UpdateSettings", payload: { logomarkImage: file, logomarkUrl: url } });
    }
  };

  const handleEntityLevelAccessChange = (value: boolean) => {
    dispatch({ type: "UpdateSettings", payload: { entityLevelAccess: value } });
  };

  const handleCancel = () => {
    dispatch({ type: "Reset" });
  };

  const demoRefreshTemplateCode = getDemoRefreshTemplateCode({
    featureEnabled: isPortalFeatureEnabled("DemoCompanyRefreshEnabled"),
    companyType,
    sourceTemplateClientCode,
  });

  return (
    <>
      <GeneralPageHeader title="General">
        {hasPermissions(["ManageOrganizationClients"]) && demoRefreshTemplateCode && (
          <RefreshDemoCompanyButton templateClientCode={demoRefreshTemplateCode} />
        )}
      </GeneralPageHeader>
      <Container maxWidth="md">
        <Stack spacing={3} sx={{ pb: 1, pt: 2 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle1">General Info</Typography>
            <Stack spacing={2} sx={(theme) => ({ width: theme.spacing(69) })}>
              <TextField disabled label="Company name" defaultValue={clientTitle} />
              <CompanyTypeSelect
                value={state.currentSettings.companyType || "Production"}
                onChange={handleCompanyTypeChange}
                disabled={!hasAccessToManageCompanySettings}
              />
              <TextField disabled label="Company URL" defaultValue={investorPortalUrl} />
            </Stack>
          </Stack>
          <Divider />
          <CompanyLogomarkSettings
            url={state.currentSettings.logomarkUrl}
            defaultUrl={state.defaultLogomarkUrl}
            onLogomarkChange={handleLogoChange}
            isLoading={state.isLoading}
            disabled={!hasAccessToManageCompanySettings}
          />
          <Divider />
          <EntityLevelAccess
            entityLevelAccessEnabled={state.currentSettings.entityLevelAccess || false}
            onChange={handleEntityLevelAccessChange}
            isLoading={state.isLoading}
            disabled={!hasAccessToManageCompanySettings}
          />
          {hasAccessToManageCompanySettings && (
            <Stack useFlexGap direction="row" spacing={1}>
              <Button
                variant="contained"
                loading={state.isLoading}
                onClick={handleSave}
                disabled={!state.formHasChanges}
              >
                Save Changes
              </Button>
              <Button variant="text" color="secondary" onClick={handleCancel} disabled={!state.formHasChanges}>
                Cancel
              </Button>
            </Stack>
          )}
        </Stack>
      </Container>
    </>
  );
};

export default GeneralCompanySettingsPage;
